import { Pagination } from "@core/domain/models/pagination";
import { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import {
    BudgetsSearchFilters,
    BudgetsSummary,
} from "@project/domain/models/budget-summary.model";
import { BudgetType } from "@project/domain/models/budget/financial-entity-budget.model";
import { FinancialEntityType } from "@project/domain/models/financial-entity/financial-entity-type";
import { CloneBudgetUseCase } from "@project/domain/usecases/budget/clone-budget.usecase";
import { DeleteBudgetUseCase } from "@project/domain/usecases/budget/delete-budget.usecase";
import { CreateBudgetFromTemplateUseCase } from "@project/domain/usecases/financial-entity/create-budget-from-template.usecase";
import { CreateBudgetFromTemplateUseCase as CreateBudgetFromBudgetTemplateUseCase } from "@project/domain/usecases/budget-template/create-budget-from-template.usecase";
import { GetAllBudgetTemplatesUseCase } from "@project/domain/usecases/budget-template/get-all-budget-templates.usecase";
import { GetAllBudgetTypesUseCase } from "@project/domain/usecases/financial-entity/get-all-budget-types.usecase";
import { GetAllFinancialEntityTypesUsecase } from "@project/domain/usecases/financial-entity/get-all-financial-entity-types.usecase";
import { GetAllBudgetsUseCase } from "@project/domain/usecases/get-all-budgets.usecase";
import { BudgetTemplate } from "@project/domain/models/budget-template/budget-template";
import { CreateBudgetFromTemplate } from "@project/domain/models/budget-template/create-budget-from-template";
import { inject, injectable } from "inversify";
import { makeObservable, observable, runInAction } from "mobx";
import { AddBudgetFromTemplateFormValidated } from "./add-budget-from-template-form";

// New interface for creating budget from specific template
export interface CreateBudgetFromTemplateData {
    templateId: number;
    name?: string;
    description?: string;
}

export type BudgetTemplateTable = {
    id: number;
    name: string;
    createdAt: string;
};
interface BudgetsTable {
    budgets: BudgetTemplateTable[];
    count: number;
}
@injectable()
export class FinancialEntityFormViewmodel extends BaseViewModel {
    _entityId: Nullable<number> = null;

    @observable
    initialLoading: boolean = true;

    @observable
    types: FinancialEntityType[] = [];

    @observable
    private templateBudgets: BudgetsSummary = new BudgetsSummary([], 0);

    @observable
    pagination: Pagination = new Pagination();

    @observable
    addBudgetFromTemplateModalOpen: boolean = false;

    @observable
    budgetTypes: BudgetType[] = [];

    @observable
    budgetTemplates: BudgetTemplate[] = [];

    @observable
    selectTemplateModalOpen: boolean = false;

    constructor(
        @inject(GetAllFinancialEntityTypesUsecase)
        private readonly getAllFinancialEntityTypesUseCase: GetAllFinancialEntityTypesUsecase,
        @inject(GetAllBudgetsUseCase)
        private readonly getAllBudgetsUseCase: GetAllBudgetsUseCase,
        @inject(DeleteBudgetUseCase)
        private readonly deleteBudgetUseCase: DeleteBudgetUseCase,
        @inject(CloneBudgetUseCase)
        private readonly cloneBudgetUseCase: CloneBudgetUseCase,
        @inject(CreateBudgetFromTemplateUseCase)
        private readonly createBudgetFromTemplateUseCase: CreateBudgetFromTemplateUseCase,
        @inject(CreateBudgetFromBudgetTemplateUseCase)
        private readonly createBudgetFromBudgetTemplateUseCase: CreateBudgetFromBudgetTemplateUseCase,
        @inject(GetAllBudgetTemplatesUseCase)
        private readonly getAllBudgetTemplatesUseCase: GetAllBudgetTemplatesUseCase,
        @inject(GetAllBudgetTypesUseCase)
        private readonly getAllBudgetTypesUseCase: GetAllBudgetTypesUseCase,
    ) {
        super();
        makeObservable(this);
    }
    override async didMount(): Promise<void> {
        await super.didMount();
        await this.initViewData();
    }
    async initViewData(): Promise<void> {
        await Promise.all([
            this.getAllFinancialEntityTypes(),
            this.getAllBudgets(this.pagination),
            this.getBudgetTypes(),
            this.getBudgetTemplates(),
        ]);
        runInAction(() => {
            this.initialLoading = false;
        });
    }
    async getAllFinancialEntityTypes(): Promise<void> {
        const types = await this.getAllFinancialEntityTypesUseCase.execute();
        runInAction(() => {
            this.types = types;
        });
    }
    async getAllBudgets(pagination: Pagination): Promise<void> {
        if (!this._entityId) return;
        const filters: BudgetsSearchFilters = {
            financialEntityId: this._entityId,
        };
        const response = await this.getAllBudgetsUseCase.execute(
            pagination,
            filters,
        );
        runInAction(() => {
            this.templateBudgets = response;
        });
    }

    async getBudgetTypes(): Promise<void> {
        if (!this._entityId) return;

        const response = await this.getAllBudgetTypesUseCase.execute();
        runInAction(() => {
            this.budgetTypes = response;
        });
    }

    async getBudgetTemplates(): Promise<void> {
        const templates = await this.getAllBudgetTemplatesUseCase.execute();
        runInAction(() => {
            this.budgetTemplates = templates;
        });
    }

    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.getAllBudgets(this.pagination);
    }

    get budgetsTable(): BudgetsTable {
        return {
            count: this.templateBudgets.count,
            budgets: this.templateBudgets.budgetSummary.map((budget) => ({
                id: budget.id,
                name: budget.name,
                createdAt: budget.createdAt.toLocaleString(),
            })),
        };
    }
    async deleteBudget(id: number): Promise<void> {
        LoadLayoutStore.start();
        const result = await this.deleteBudgetUseCase.execute(id);
        if (result) {
            ToastManagerStore.success();
            await this.getAllBudgets(this.pagination);
        }
        LoadLayoutStore.finish();
    }

    async cloneBudget(id: number): Promise<void> {
        LoadLayoutStore.start();
        const result = await this.cloneBudgetUseCase.execute(id);
        if (result) {
            ToastManagerStore.success();
            await this.getAllBudgets(this.pagination);
        }
        LoadLayoutStore.finish();
    }

    setAddBudgetFromTemplateModalOpen(isOpen: boolean): void {
        this.addBudgetFromTemplateModalOpen = isOpen;
    }

    setSelectTemplateModalOpen(isOpen: boolean): void {
        this.selectTemplateModalOpen = isOpen;
    }

    async createBudgetFromTemplate(templateId: number): Promise<void> {
        if (this._entityId) {
            LoadLayoutStore.start();

            // Find the selected template to get its name and description
            const selectedTemplate = this.budgetTemplates.find(t => t.id === templateId);

            if (!selectedTemplate) {
                ToastManagerStore.error("Plantilla no encontrada");
                LoadLayoutStore.finish();
                return;
            }

            // Generate unique name by adding timestamp or counter
            const timestamp = new Date().toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }).replace(/[/:]/g, '');

            const uniqueName = `${selectedTemplate.name} (Copia ${timestamp})`;

            // Create budget from template using the correct UseCase
            const createBudgetFromTemplate: CreateBudgetFromTemplate = {
                template_id: templateId,
                name: uniqueName,
                description: selectedTemplate.description || "",
                finantial_entity: this._entityId, // Assign to current financial entity
            };

            const result = await this.createBudgetFromBudgetTemplateUseCase.execute(
                createBudgetFromTemplate
            );

            if (result.isRight()) {
                ToastManagerStore.success();
                await this.getAllBudgets(this.pagination);
                this.selectTemplateModalOpen = false;
            } else {
                ToastManagerStore.error(result.getLeftOrThrow().message);
            }
            LoadLayoutStore.finish();
        }
    }

    async addBudgetFromTemplate(
        budgetType: AddBudgetFromTemplateFormValidated,
    ): Promise<void> {
        if (this._entityId) {
            LoadLayoutStore.start();

            const result = await this.createBudgetFromTemplateUseCase.execute(
                budgetType,
                this._entityId,
            );

            if (result) {
                ToastManagerStore.success();
                await this.getAllBudgets(this.pagination);
                this.addBudgetFromTemplateModalOpen = false;
            }
            LoadLayoutStore.finish();
        }
    }
}
